/**
 * Keycloak
 */

import { keycloakInstance } from './index';

/**
 * Apollo
 */

import apolloClient from '@core/apollo';

/**
 * Stores
 */

import { useKeycloakStore } from '@/stores/keycloak';
import { resetSessionStores } from '@/stores/resetSessionStores';

/**
 * Types
 */

import type { KeycloakInitOptions, KeycloakTokenParsed } from 'keycloak-js';

const KEYCLOAK_SSO_SESSION_IDLE =
  import.meta.env.VITE_APP_KEYCLOAK_SESSION_IDLE * 60; // convert to seconds
const KEYCLOAK_SSO_SESSION_REFRESH_THRESHOLD = KEYCLOAK_SSO_SESSION_IDLE - 60; // subtract a minute in order to allow concurrent request to work with the same token within a minute

function getKeycloak() {
  return keycloakInstance;
}

async function getToken() {
  return updateToken();
}

async function updateToken() {
  const { setToken } = useKeycloakStore();

  if (!keycloakInstance) {
    throw new Error('Keycloak is not initialized');
  }

  /**
   * If the current user performs action within
   * the KEYCLOAK_SSO_SESSION_REFRESH_THRESHOLD seconds window,
   * it's token will be refreshed
   */
  try {
    const refreshed = await keycloakInstance.updateToken(
      KEYCLOAK_SSO_SESSION_REFRESH_THRESHOLD,
    );

    if (refreshed && keycloakInstance.token) {
      setToken(keycloakInstance.token);
      return keycloakInstance.token;
    }
    return refreshed;
  } catch (err) {
    console.error(
      'Failed to refresh the token, or the session has expired',
      err,
    );
    return false;
  }
}

async function initKeycloak(initOptions: KeycloakInitOptions) {
  const { setKeycloakData, setIsAuthenticated, setToken, clearStore } =
    useKeycloakStore();

  try {
    const isAuthenticated = await keycloakInstance.init(initOptions);
    let token = '';
    let tokenParsed: KeycloakTokenParsed = {};

    if (!isAuthenticated) {
      await keycloakInstance.login();
      return;
    }
    setIsAuthenticated(isAuthenticated);

    if (keycloakInstance.token && keycloakInstance.tokenParsed) {
      token = keycloakInstance.token;
      tokenParsed = keycloakInstance.tokenParsed;

      setToken(keycloakInstance.token);
      setKeycloakData(keycloakInstance.tokenParsed);
    }

    keycloakInstance.onAuthRefreshSuccess = () => {
      setIsAuthenticated(true);
      setToken(token);
      setKeycloakData(tokenParsed);
    };

    keycloakInstance.onAuthRefreshError = () => {
      setIsAuthenticated(false);
      setToken('');
      clearStore();
      resetSessionStores();
    };

    keycloakInstance.onTokenExpired = async () => {
      const updated = await updateToken();
      if (!updated) {
        await apolloClient.clearStore();
        keycloakInstance.clearToken();
      }
    };

    keycloakInstance.onAuthLogout = async () => {
      resetSessionStores();
      await keycloakInstance.logout();
    };
  } catch {
    setIsAuthenticated(false);
  }
}

export { initKeycloak, getKeycloak, getToken, updateToken };
